import { z } from 'zod';
import { optionalText, requiredText } from './field.js';
/**
 * Documentation attached to a contact, optionally to an activity. Files hang
 * off a note (`note_file`), never off a contact or an activity directly — that
 * gives attachments the same locking semantics as the text without a second
 * mechanism (rule 7 in `.claude/rules/notes.md`).
 *
 * **The type is a catalogue entry** since migration 0038 — `note_type`, see
 * `note-type.ts`. It was `text` with a named check constraint over six fixed
 * values before that, and this file used to explain why that was the right
 * shape; it was the second-best one. The set is not merely expected to change,
 * it is maintained by the practitioner, so a note points at a row through a
 * composite foreign key, the way `activity.type` and the contact's own three
 * fields do.
 *
 * `noteTypeLabel` travels beside the id and is stored nowhere: it is joined on
 * read, like `createdByName` and like `contactName` on an activity. Renaming a
 * type therefore reaches every note at once, which is the whole point of
 * having no code.
 *
 * **An addendum is no longer a type.** It is a note with a `correctsNoteId`,
 * and it carries the type of the note it supplements, fixed when it is
 * written (N1b) — an addendum to a session note is itself session
 * documentation. The refinement that used to tie the two together went with
 * the check constraint `note_addendum_target`.
 *
 * **A note has a status since N1**: `draft` → `final` → `locked`, forward
 * only. The title is required in every status (N1b) — a note is written with
 * one prefilled, so there is nothing to leave blank. The text is optional in
 * every status — a note may be a title and its attachments. Locking has its
 * own route, so a save can only ever ask for `draft` or `final`.
 *
 * **A note exists from the moment it is started** (N1b): "Neue Notiz",
 * "Nachtrag" and "Dokumentieren" create a draft at once, prefilled by the
 * server, and the form edits that row. So there is one way to create a note,
 * `noteStartSchema`, and it carries no content at all.
 */
export const noteStatuses = ['draft', 'final', 'locked'];
export const noteStatusSchema = z.enum(noteStatuses);
/** What saving may ask for. `locked` is reached only through
 *  `POST /api/notes/:noteId/lock`, and the schema says so by not having it. */
export const noteSaveStatusSchema = z.enum(['draft', 'final']);
/** A title is a line, not prose. */
export const MAX_NOTE_TITLE = 200;
/** The upper bound on a note's text. Generous — a session note is prose, not a
 *  form field — but not unbounded, so one paste cannot fill the disk. */
export const MAX_NOTE_TEXT = 20_000;
/**
 * Starting a note (N1b): who it is about, and what it is for. Everything else
 * — type, date, title — the server fills in, so the draft exists before the
 * form opens and the form never creates anything.
 *
 * An addendum takes its activity from the note it supplements, so the two are
 * not sent together.
 */
export const noteStartSchema = z
    .object({
    contactId: z.uuid(),
    /** Set by "Dokumentieren": the note is about this activity. */
    activityId: z.uuid().nullable().default(null),
    /** Set exactly when this note supplements a locked one. Nothing else marks
     *  an addendum. */
    correctsNoteId: z.uuid().nullable().default(null),
})
    .refine((value) => value.activityId === null || value.correctsNoteId === null, {
    message: 'an addendum takes its activity from the note it supplements',
    path: ['activityId'],
});
/** What a save carries. The title is trimmed and required in every status; the
 *  text is trimmed and folded to `null` when blank — so whatever the editor
 *  emits for an emptied document, nothing blank reaches the row.
 *
 *  Contact and addendum target do not move once the note exists. The type
 *  does, except on an addendum — the domain refuses that. So does the status,
 *  forward only; the domain refuses `final` → `draft`. */
export const noteUpdateSchema = z.object({
    /** Optional — a note may document a session or stand on its own. */
    activityId: z.uuid().nullable().default(null),
    /** The day being documented, which is not always the day of writing. */
    noteDate: z.iso.date(),
    noteTypeId: z.uuid(),
    title: requiredText(MAX_NOTE_TITLE),
    text: optionalText(MAX_NOTE_TEXT),
    status: noteSaveStatusSchema,
});
/**
 * The types a browser may show instead of download (N2), and only on request
 * (`?disposition=inline`). Each is rendered by the browser's own viewer and
 * none can script the page around it. One list, read by the server that
 * decides the header and by the client that decides where a link opens.
 *
 * Deliberately not in it: SVG, which can carry script and is therefore always
 * an attachment (it is shown through `<img>`, where scripts do not run), and
 * HEIC and TIFF, which most browsers cannot render anyway.
 */
export const INLINE_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
];
/** `storage_path` and `thumbnail_path` deliberately do not travel: where the
 *  bytes sit on disk is nobody's business outside the server. Whether there is
 *  a thumbnail does, so the client need not guess (N2). */
export const noteFileSchema = z.object({
    id: z.uuid(),
    noteId: z.uuid(),
    fileName: z.string(),
    /** Detected from the bytes on upload, `application/octet-stream` when the
     *  type is not one the server knows. */
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    sha256: z.string(),
    hasThumbnail: z.boolean(),
    createdAt: z.iso.datetime(),
});
/** How large one attachment may be — `NOTE_FILE_MAX_MB` on the server, handed
 *  to the client so it can refuse a file before sending it (N2). */
export const noteFileLimitSchema = z.object({ maxBytes: z.number().int().positive() });
/** The longest original file name an upload may carry, decoded. */
export const MAX_NOTE_FILE_NAME = 255;
export const noteSchema = z.object({
    id: z.uuid(),
    contactId: z.uuid(),
    activityId: z.uuid().nullable(),
    noteDate: z.iso.date(),
    noteTypeId: z.uuid(),
    /** Joined on read, stored nowhere — see the note at the top of this file. */
    noteTypeLabel: z.string(),
    title: z.string(),
    text: z.string().nullable(),
    status: noteStatusSchema,
    createdBy: z.uuid(),
    createdByName: z.string(),
    createdAt: z.iso.datetime(),
    lockedAt: z.iso.datetime().nullable(),
    lockedByName: z.string().nullable(),
    contentHash: z.string().nullable(),
    prevHash: z.string().nullable(),
    correctsNoteId: z.uuid().nullable(),
    files: z.array(noteFileSchema),
});
/** Either by contact or by activity; the route requires one, so a bare call
 *  cannot walk the whole documentation. */
export const noteListQuerySchema = z
    .object({
    contactId: z.uuid().optional(),
    activityId: z.uuid().optional(),
})
    .refine((query) => query.contactId !== undefined || query.activityId !== undefined, {
    message: 'contactId or activityId is required',
});
/**
 * The verification report.
 *
 * Content and files are reported apart on purpose: they have different causes
 * and different consequences. A content mismatch means the row itself was
 * altered — the hash no longer follows from what is stored. A file mismatch
 * means the row is intact and the bytes behind it are not, which is a swapped
 * or corrupted attachment. A link mismatch means the chain was cut between two
 * notes, typically because a note was deleted or inserted after the fact.
 */
export const noteFileCheckSchema = z.object({
    fileId: z.uuid(),
    fileName: z.string(),
    status: z.enum(['ok', 'mismatch', 'missing']),
});
export const noteChainEntrySchema = z.object({
    noteId: z.uuid(),
    noteDate: z.iso.date(),
    lockedAt: z.iso.datetime(),
    /** The recomputed content hash equals the stored one. */
    contentOk: z.boolean(),
    /** `prev_hash` points at the predecessor's stored hash. */
    linkOk: z.boolean(),
    /** Empty when the files were not read — see `checkedFiles`. */
    files: z.array(noteFileCheckSchema),
});
export const noteChainReportSchema = z.object({
    contactId: z.uuid(),
    /** False when only the database was checked, which is the parameter the UI
     *  never sets — it always asks for the full check. */
    checkedFiles: z.boolean(),
    entries: z.array(noteChainEntrySchema),
});
export function chainEntryOk(entry) {
    return entry.contentOk && entry.linkOk && entry.files.every((file) => file.status === 'ok');
}
export function chainOk(report) {
    return report.entries.every(chainEntryOk);
}
//# sourceMappingURL=note.js.map